// src/App.jsx
import React, { useState, useEffect, lazy, Suspense, useContext } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { AuthProvider, AuthContext } from "./contexts/AuthContext";
import SmoothScroll from "smooth-scroll";
import JsonData from "./data/data.json";
import "aos/dist/aos.css";

// Components chargés immédiatement (critiques)
import Navigation from "./components/navigation";
import { Header } from "./components/header";
import { Footer } from "./components/footer";
import Animation from './components/Animation';
// import { Origami } from "lucide-react";

// Lazy loading pour les composants non critiques
const Features = lazy(() => import("./components/features").then(m => ({ default: m.Features })));
const About = lazy(() => import("./components/about").then(m => ({ default: m.About })));
const Services = lazy(() => import("./components/services").then(m => ({ default: m.Services })));
// const Team = lazy(() => import("./components/Team"));
const Contact = lazy(() => import("./components/contact").then(m => ({ default: m.Contact })));
const References = lazy(() => import("./components/References"));
// const ProductsPage = lazy(() => import("./components/ProductsPage"));
const History = lazy(() => import("./components/History"));
const AboutPage = lazy(() => import("./components/AboutPage").then(m => ({ default: m.AboutPage })));
const PresentationOrga = lazy(() => import("./components/PresentationOrga"));
const Register = lazy(() => import("./components/Register"));
const Login = lazy(() => import("./components/Login"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./components/AdminLogin"));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Privacy = lazy(() => import('./components/Privacy'));
const Terms = lazy(() => import('./components/Terms'));
const Legal = lazy(() => import('./components/Legal'));
//const Formation = lazy(() => import('./components/Formation'));
const Entreprise = lazy(() => import('./components/Entreprise'));
const Modulaire = lazy(() => import('./components/Modulaire'));
// const organization = lazy(() => import('./components/organization'));

// Initialiser AOS avec gestion d'erreurs
try {
  const AOS = require('aos');
  AOS.init({
    duration: 1000,
    once: true,
    easing: 'ease',
  });
} catch (e) {
  console.warn("AOS library failed to load. Animations might not work properly.", e);
}

// Initialiser SmoothScroll
export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

// Composant de chargement
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh'
  }}>
    <div className="loading-spinner">Chargement...</div>
  </div>
);

// Page d'accueil
const HomePage = ({ landingPageData }) => (
  <Suspense fallback={<LoadingFallback />}>
    <Header data={landingPageData.Header} />
    <Features data={landingPageData.Features} />
    <About data={landingPageData.About} />
    <Services data={landingPageData.Services} />
  </Suspense>
);

// Layout principal
const MainLayout = ({ children }) => (
  <>
    <Navigation />
    <Suspense fallback={<LoadingFallback />}>
      {children}
    </Suspense>
    <Footer />
  </>
);

// Route privée
const PrivateRoute = ({ component: Component, ...rest }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Suspense fallback={<LoadingFallback />}>
            <Component {...props} />
          </Suspense>
        ) : (
          <Redirect
            to={{
              pathname: "/admin/connexion",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

// Composant Route avec Lazy Loading
const LazyRoute = ({ component: Component, layout: Layout = MainLayout, ...rest }) => (
  <Route
    {...rest}
    render={(props) => (
      <Layout>
        <Suspense fallback={<LoadingFallback />}>
          <Component {...props} />
        </Suspense>
      </Layout>
    )}
  />
);

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [showAnimation, setShowAnimation] = useState(true);

  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  // Afficher l'animation au chargement initial
  if (showAnimation) {
    return <Animation onComplete={() => setShowAnimation(false)} />;
  }
  ;


  return (
    <AuthProvider>
      <Router>
        <Switch>
          {/* Page d'accueil */}
          <Route exact path="/">
            <MainLayout>
              <HomePage landingPageData={landingPageData} />
            </MainLayout>
          </Route>

          {/* Routes principales */}
          {/* <LazyRoute path="/produits" component={ProductsPage} /> */}
          <LazyRoute path="/references" component={References} />
          <LazyRoute path="/services" component={Services} />
          {/* <LazyRoute path="/formation" component={Formation} /> */}
          {/* <LazyRoute path="/modulaire" component={Modulaire} /> */}
          {/* <LazyRoute path="/entreprise" component={Entreprise} /> */}
          <LazyRoute path="/a-propos" component={AboutPage} />
          <LazyRoute path="/historique" component={History} />
          <LazyRoute path="/presentation" component={PresentationOrga} />
          <LazyRoute
            path="/contact"
            component={() => <Contact props={landingPageData.Contact} />}
          />
          <Route path="/organization">
            <Redirect to="/presentation" />
          </Route>
          {/* <LazyRoute path="/equipe" component={Team} /> */}

          {/* Authentification */}
          <LazyRoute path="/inscription" component={Register} />
          <LazyRoute path="/connexion" component={Login} />

          {/* Admin (sans MainLayout) */}
          <Route path="/admin/connexion">
            <Suspense fallback={<LoadingFallback />}>
              <AdminLogin />
            </Suspense>
          </Route>
          <PrivateRoute path="/admin/dashboard" component={AdminDashboard} />

          {/* Vérification email */}
          <Route path="/verification-email/succes">
            <Suspense fallback={<LoadingFallback />}>
              <EmailVerification status="success" />
            </Suspense>
          </Route>
          <Route path="/verification-email/erreur">
            <Suspense fallback={<LoadingFallback />}>
              <EmailVerification status="error" />
            </Suspense>
          </Route>
          <Route path="/verification-email/deja-verifie">
            <Suspense fallback={<LoadingFallback />}>
              <EmailVerification status="already-verified" />
            </Suspense>
          </Route>
          <Route path="/verification-email/:token">
            <Suspense fallback={<LoadingFallback />}>
              <EmailVerification />
            </Suspense>
          </Route>

          {/* Mot de passe */}
          <LazyRoute path="/mot-de-passe-oublie" component={ForgotPassword} layout={React.Fragment} />
          <Route path="/reinitialiser-mot-de-passe/:token">
            <Suspense fallback={<LoadingFallback />}>
              <ResetPassword />
            </Suspense>
          </Route>

          {/* Pages légales */}
       {/* <LazyRoute path="/confidentialite" component={Privacy} />
          <LazyRoute path="/conditions" component={Terms} />
          <LazyRoute path="/mentions-legales" component={Legal} /> */}

          {/* Redirection pour routes non trouvées */}
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </AuthProvider>
  );
};

export default App;